import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { SEOHead } from '../components/ui/SEOHead'
import { TerminalWindow } from '../components/terminal/TerminalWindow'
import { TypewriterText } from '../components/terminal/TypewriterText'
import { TerminalPrompt } from '../components/terminal/TerminalPrompt'
import { AsciiDivider } from '../components/terminal/AsciiDivider'

const ASCII_LOGO = `
  ____          _    _______    ____        _
 / ___|___   __| |__|___ / __ )_   _|__ _ | |_ ___
| |   / _ \\ / _\` / _ \\|_ \\|  _ \\| |/ _\` || __/ _ \\
| |__| (_) | (_| |  __/___) | |_) | | (_| || ||  __/
 \\____\\___/ \\__,_|\\___|____/|____/|_|\\__, | \\__\\___|
                                        |_|
`.trim()

const TOPICS = [
  { tag: '.NET & C#',               desc: 'ASP.NET Core, EF Core, performance patterns, and the CLR.' },
  { tag: 'Java',                    desc: 'Spring Boot, JVM internals, concurrency, and the ecosystem.' },
  { tag: 'AI & ML',                 desc: 'Practical AI integration, LLMs, embeddings, and inference.' },
  { tag: 'Developer Tools',         desc: 'IDEs, CLIs, debugging, profiling, and productivity workflows.' },
  { tag: 'Metrics & Observability', desc: "Logging, tracing, dashboards, and knowing what's wrong before your users do." },
  { tag: 'Systems Design',          desc: 'Distributed systems, scalability, consistency, and trade-offs.' },
  { tag: 'DevOps & Cloud',          desc: 'CI/CD, containers, Kubernetes, Azure, and infrastructure as code.' },
  { tag: 'Architecture',            desc: 'Clean architecture, DDD, CQRS, and API design.' },
  { tag: 'Security',                desc: 'Auth, encryption, threat modelling, and secure coding practices.' },
  { tag: 'Databases',               desc: 'SQL, NoSQL, search engines, query optimisation, and data modelling.' },
]

export const AboutPage: React.FC = () => {
  const [introComplete, setIntroComplete] = useState(false)

  return (
    <>
      <SEOHead
        title="About"
        description="About CodeToByte — a technical blog covering .NET, Java, AI, systems design, DevOps, and more. New articles every week."
        url="/about"
      />

      <div style={{ paddingTop: '32px', maxWidth: '780px', margin: '0 auto' }}>
        <TerminalWindow title="about.md">
          <div
            style={{
              fontFamily: "'JetBrains Mono', 'Courier New', monospace",
              fontSize: '0.85rem',
              lineHeight: '1.8',
            }}
          >
            {/* ASCII Logo */}
            <pre
              style={{
                color: 'var(--accent-green)',
                fontSize: '0.65rem',
                lineHeight: '1.3',
                marginBottom: '24px',
                overflow: 'auto',
              }}
              aria-hidden="true"
            >
              {ASCII_LOGO}
            </pre>

            {/* Typewriter intro */}
            <div style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
              <TerminalPrompt path="~/about" command="cat intro.txt" />
              <div style={{ marginTop: '12px', fontSize: '0.92rem' }}>
                <TypewriterText
                  text="Engineering articles, written by engineers, for engineers."
                  speed={40}
                  onComplete={() => setIntroComplete(true)}
                />
              </div>
            </div>

            {introComplete && (
              <>
                <AsciiDivider char="─" width={50} />

                {/* What is CodeToByte */}
                <section style={{ marginBottom: '24px' }}>
                  <div style={{ color: 'var(--accent-cyan)', marginBottom: '8px' }}>
                    ## What is CodeToByte?
                  </div>
                  <p style={{ color: 'var(--text-secondary)', margin: '0 0 12px', fontSize: '0.875rem' }}>
                    CodeToByte is a technical blog written by a community of software engineers
                    who believe that great writing and great engineering go hand in hand. We
                    publish in-depth articles on backend development, distributed systems,
                    artificial intelligence, developer tooling, and everything in between.
                  </p>
                  <p style={{ color: 'var(--text-secondary)', margin: '0', fontSize: '0.875rem' }}>
                    Every post is a real deep dive — no shallow overviews recycled from the docs,
                    no clickbait. Just honest writing, real code, and the reasoning behind
                    the decisions.
                  </p>
                </section>

                <AsciiDivider char="─" width={50} />

                {/* Topics */}
                <section style={{ marginBottom: '24px' }}>
                  <div style={{ color: 'var(--accent-cyan)', marginBottom: '12px' }}>
                    ## What We Cover
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {TOPICS.map(({ tag, desc }) => (
                      <div
                        key={tag}
                        style={{ display: 'flex', gap: '0', flexWrap: 'wrap', alignItems: 'baseline' }}
                      >
                        <span
                          style={{
                            color: 'var(--accent-green)',
                            minWidth: '230px',
                            fontSize: '0.82rem',
                            flexShrink: 0,
                          }}
                        >
                          {tag}
                        </span>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          — {desc}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <AsciiDivider char="─" width={50} />

                {/* Publishing cadence */}
                <section style={{ marginBottom: '24px' }}>
                  <div style={{ color: 'var(--accent-cyan)', marginBottom: '8px' }}>
                    ## Publishing Schedule
                  </div>
                  <p style={{ color: 'var(--text-secondary)', margin: '0 0 10px', fontSize: '0.875rem' }}>
                    We publish{' '}
                    <strong style={{ color: 'var(--text-primary)' }}>new articles every week</strong>.
                    Each post goes through a review process to ensure technical accuracy and
                    clarity before publication.
                  </p>
                  <p style={{ color: 'var(--text-secondary)', margin: '0', fontSize: '0.875rem' }}>
                    Subscribe to the newsletter below to get new articles delivered straight
                    to your inbox — no spam, one-click unsubscribe.
                  </p>
                </section>

                <AsciiDivider char="─" width={50} />

                {/* Contributors */}
                <section style={{ marginBottom: '24px' }}>
                  <div style={{ color: 'var(--accent-cyan)', marginBottom: '8px' }}>
                    ## Contributors
                  </div>
                  <p style={{ color: 'var(--text-secondary)', margin: '0 0 10px', fontSize: '0.875rem' }}>
                    CodeToByte is a multi-contributor publication. Articles are written by
                    engineers from different backgrounds, specialisms, and industries — all
                    united by a passion for clear technical thinking.
                  </p>
                  <p style={{ color: 'var(--text-secondary)', margin: '0', fontSize: '0.875rem' }}>
                    A contributors page with author profiles is coming soon.
                  </p>
                </section>

                <AsciiDivider char="─" width={50} />

                {/* Support & Feedback */}
                <section style={{ marginBottom: '24px' }}>
                  <div style={{ color: 'var(--accent-cyan)', marginBottom: '10px' }}>
                    ## Get in Touch
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '0', flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <span
                        style={{
                          color: 'var(--accent-green)',
                          minWidth: '120px',
                          fontSize: '0.82rem',
                          flexShrink: 0,
                        }}
                      >
                        support
                      </span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                        {'→ '}
                        <a
                          href="mailto:support@codetobyte.com"
                          style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
                        >
                          support@codetobyte.com
                        </a>
                        {' (account help, privacy requests, content removal)'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0', flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <span
                        style={{
                          color: 'var(--accent-green)',
                          minWidth: '120px',
                          fontSize: '0.82rem',
                          flexShrink: 0,
                        }}
                      >
                        feedback
                      </span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                        {'→ '}
                        <a
                          href="mailto:feedback@codetobyte.com"
                          style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
                        >
                          feedback@codetobyte.com
                        </a>
                        {' (article ideas, corrections, suggestions)'}
                      </span>
                    </div>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', margin: '14px 0 0', fontSize: '0.82rem' }}>
                    We read every message and aim to respond within a few business days.
                  </p>
                </section>

                <AsciiDivider char="─" width={50} />

                {/* Legal */}
                <section style={{ marginBottom: '24px' }}>
                  <div style={{ color: 'var(--accent-cyan)', marginBottom: '8px' }}>
                    ## Legal
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                      <Link
                        to="/privacy"
                        style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
                      >
                        Privacy Policy
                      </Link>
                      {' — how we collect, use, and protect your information.'}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                      <Link
                        to="/terms"
                        style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
                      >
                        Terms and Conditions
                      </Link>
                      {' — community rules, comment policy, and your rights.'}
                    </div>
                  </div>
                </section>

                <AsciiDivider char="─" width={50} />

                <div style={{ color: 'var(--text-muted, #555)', fontSize: '0.8rem' }}>
                  <TerminalPrompt path="~/about" showCursor={true} />
                </div>
              </>
            )}
          </div>
        </TerminalWindow>
      </div>
    </>
  )
}
